/**
 * Wave 1 / #1861 F-04 — owner-bound offline envelope helpers.
 *
 * PHI-capable offline rows must carry tenant/user ownership and refuse
 * cross-identity read/sync. Encryption uses AES-GCM when WebCrypto is available;
 * when unavailable (SSR/tests without crypto.subtle), plaintext is refused for
 * PHI-capable payloads unless allowPlaintextForTests is set.
 */

export type OfflineOwner = {
  userId: string
  tenantId: string
}

export type OfflineEnvelopeV1 = {
  v: 1
  owner_user_id: string
  owner_tenant_id: string
  created_at: string
  /** base64 ciphertext when encrypted; omitted for non-PHI structural metadata only */
  ciphertext_b64?: string
  iv_b64?: string
  /** Legacy plaintext content — only accepted for migration/quarantine path */
  legacy_plaintext?: unknown
}

const TEXT_ENCODER = new TextEncoder()
const TEXT_DECODER = new TextDecoder()

export function ownersMatch(a: OfflineOwner | null | undefined, b: OfflineOwner | null | undefined): boolean {
  if (!a || !b) return false
  return a.userId === b.userId && a.tenantId === b.tenantId
}

export function assertOwnerMatch(
  rowOwner: OfflineOwner,
  activeOwner: OfflineOwner | null | undefined,
): void {
  if (!ownersMatch(rowOwner, activeOwner)) {
    throw new Error('offline_owner_mismatch')
  }
}

export function isLegacyUnownedRow(row: Record<string, unknown>): boolean {
  const ownerId = row.owner_id
  if (ownerId === 'viewer_context_id' || ownerId === '' || ownerId == null) return true
  if (!row.owner_user_id || !row.owner_tenant_id) return true
  return false
}

function b64Encode(bytes: Uint8Array): string {
  let binary = ''
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]!)
  return btoa(binary)
}

function b64Decode(b64: string): Uint8Array {
  const binary = atob(b64)
  const out = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i)
  return out
}

/**
 * Derive a non-exportable AES-GCM key from owner identity + session material.
 * Not a substitute for proper KMS; binds ciphertext to identity so raw dump
 * without the active session key material is not immediately readable.
 */
export async function deriveOwnerKey(
  owner: OfflineOwner,
  sessionMaterial: string,
): Promise<CryptoKey> {
  if (!globalThis.crypto?.subtle) {
    throw new Error('webcrypto_unavailable')
  }
  const material = TEXT_ENCODER.encode(`${owner.tenantId}:${owner.userId}:${sessionMaterial}`)
  const hash = await crypto.subtle.digest('SHA-256', material)
  return crypto.subtle.importKey('raw', hash, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt'])
}

export async function encryptPayload(
  owner: OfflineOwner,
  sessionMaterial: string,
  payload: unknown,
): Promise<OfflineEnvelopeV1> {
  const key = await deriveOwnerKey(owner, sessionMaterial)
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const plain = TEXT_ENCODER.encode(JSON.stringify(payload))
  const cipherBuf = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, plain)
  return {
    v: 1,
    owner_user_id: owner.userId,
    owner_tenant_id: owner.tenantId,
    created_at: new Date().toISOString(),
    ciphertext_b64: b64Encode(new Uint8Array(cipherBuf)),
    iv_b64: b64Encode(iv),
  }
}

export async function decryptPayload(
  envelope: OfflineEnvelopeV1,
  activeOwner: OfflineOwner,
  sessionMaterial: string,
): Promise<unknown> {
  assertOwnerMatch(
    { userId: envelope.owner_user_id, tenantId: envelope.owner_tenant_id },
    activeOwner,
  )
  if (!envelope.ciphertext_b64 || !envelope.iv_b64) {
    throw new Error('offline_missing_ciphertext')
  }
  const key = await deriveOwnerKey(activeOwner, sessionMaterial)
  const iv = b64Decode(envelope.iv_b64)
  const cipher = b64Decode(envelope.ciphertext_b64)
  // Ensure ArrayBuffer-backed views for subtle crypto typings
  const ivCopy = new Uint8Array(iv)
  const cipherCopy = new Uint8Array(cipher)
  const plainBuf = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: ivCopy },
    key,
    cipherCopy,
  )
  return JSON.parse(TEXT_DECODER.decode(plainBuf))
}

/** Refuse sync when row owner does not match active identity. */
export function maySyncRow(
  rowOwner: OfflineOwner | null | undefined,
  activeOwner: OfflineOwner | null | undefined,
): boolean {
  if (!activeOwner) return false
  if (!rowOwner) return false
  return ownersMatch(rowOwner, activeOwner)
}
